import type { ReactElement } from 'react'
import { DestinationButton } from './DestinationButton'
import type { DestinationButtonStation, DestinationSlot } from './DestinationButton'
import { SwapButton } from './SwapButton'
import { MyLocationButton } from './MyLocationButton'
import styles from './DestinationButtons.module.css'

/**
 * Layout variant for the {@link DestinationButtons} row.
 *
 * - `'phone'` is the compact mobile shape: two equal-width buttons sit
 *   side-by-side, the SwapButton is omitted, and per-button X-clear icons are
 *   suppressed (touch users use long-press / context menu instead).
 * - `'tablet'` is the desktop / tablet shape: From above To stacked vertically
 *   with a {@link SwapButton} pinned next to the column, and per-button
 *   X-clear icons enabled for pointer affordance.
 */
export type RouteCardVariant = 'phone' | 'tablet'

/**
 * Props for {@link DestinationButtons}.
 *
 * The component is purely presentational in this task; tap wiring lands later.
 */
export interface DestinationButtonsProps {
  /** Layout variant — see {@link RouteCardVariant}. */
  readonly variant: RouteCardVariant
  /** Selected "from" station, or `null` for the empty placeholder state. */
  readonly from: DestinationButtonStation | null
  /** Selected "to" station, or `null` for the empty placeholder state. */
  readonly to: DestinationButtonStation | null
  /**
   * Placeholder text for the empty "from" button.
   *
   * @defaultValue `'From'`
   */
  readonly fromPlaceholder?: string
  /**
   * Placeholder text for the empty "to" button.
   *
   * @defaultValue `'To'`
   */
  readonly toPlaceholder?: string
  /**
   * Called when the user activates the X clear icon on the "from" pill.
   *
   * Only fires on the `tablet` variant when the "from" slot is filled —
   * that's the only state in which the X is rendered. The `phone` variant
   * suppresses the X entirely.
   */
  readonly onFromClear?: () => void
  /**
   * Called when the user activates the X clear icon on the "to" pill.
   *
   * See {@link DestinationButtonsProps.onFromClear} for behaviour notes.
   */
  readonly onToClear?: () => void
  /**
   * Called when the user activates the SwapButton (tablet variant only —
   * the `phone` variant does not render the swap control).
   */
  readonly onSwap?: () => void
  /**
   * Called when the user taps the "from" pill body. Wired by the host
   * to open Suggester for the `from` slot (TASK-318).
   */
  readonly onFromTap?: () => void
  /**
   * Called when the user taps the "to" pill body. Wired by the host to
   * open Suggester for the `to` slot (TASK-318).
   */
  readonly onToTap?: () => void
  /**
   * Called when the user taps the "my location" button (TASK-391).
   *
   * When provided, a round location-pin button is rendered alongside the
   * destination row on both variants. When omitted the button is not
   * rendered at all — keeping legacy call sites byte-for-byte unchanged.
   */
  readonly onMyLocation?: () => void
  /**
   * `true` while a "my location" fix is resolving — drives the spinner on
   * the my-location button and blocks re-taps (TASK-391 §3.3). Defaults
   * `false` so existing call sites are unchanged.
   */
  readonly isResolvingMyLocation?: boolean
  /**
   * Pills-as-search active slot (TASK-412, tablet/side-panel only). When
   * set, that pill becomes the Suggester query input (caret + typing).
   * `null`/omitted → no pill is in search mode (the legacy tap-to-open
   * behaviour). Phone variant ignores this entirely.
   */
  readonly activeSearchSlot?: DestinationSlot | null
  /** Controlled query text for the active search pill (TASK-412). */
  readonly searchQuery?: string
  /** Fired on each keystroke in the active search pill (TASK-412). */
  readonly onSearchQueryChange?: (query: string) => void
}

/**
 * Two-button layout for the From → To destination row of the RouteCard.
 *
 * Composes a pair of {@link DestinationButton}s and, on the `tablet` variant,
 * a {@link SwapButton}.
 *
 * - `phone`: side-by-side row, no SwapButton, no X-clear icons.
 * - `tablet`: vertical column of From above To plus SwapButton, X-clear icons
 *   enabled (per-button — only the *filled* slots actually render an X).
 *
 * Default placeholders are `"From"` and `"To"`.
 */
export function DestinationButtons({
  variant,
  from,
  to,
  fromPlaceholder = 'From',
  toPlaceholder = 'To',
  onFromClear,
  onToClear,
  onSwap,
  onFromTap,
  onToTap,
  onMyLocation,
  isResolvingMyLocation = false,
  activeSearchSlot = null,
  searchQuery = '',
  onSearchQueryChange,
}: DestinationButtonsProps): ReactElement {
  if (variant === 'phone') {
    return (
      <div className={styles.phoneRow}>
        <DestinationButton
          slot="from"
          station={from}
          placeholder={fromPlaceholder}
          variant={variant}
          onClear={onFromClear}
          onTap={onFromTap === undefined ? undefined : () => onFromTap()}
        />
        <DestinationButton
          slot="to"
          station={to}
          placeholder={toPlaceholder}
          variant={variant}
          onClear={onToClear}
          onTap={onToTap === undefined ? undefined : () => onToTap()}
        />
        {onMyLocation !== undefined && (
          <MyLocationButton
            variant="phone"
            onClick={onMyLocation}
            isLoading={isResolvingMyLocation}
          />
        )}
        <SwapButton variant="phone" onClick={onSwap} />
      </div>
    )
  }

  return (
    <div className={styles.tabletRow}>
      <div className={styles.column}>
        <DestinationButton
          slot="from"
          station={from}
          placeholder={fromPlaceholder}
          variant={variant}
          onClear={onFromClear}
          onTap={onFromTap === undefined ? undefined : () => onFromTap()}
          editable={activeSearchSlot === 'from'}
          query={searchQuery}
          onQueryChange={onSearchQueryChange}
        />
        <DestinationButton
          slot="to"
          station={to}
          placeholder={toPlaceholder}
          variant={variant}
          onClear={onToClear}
          onTap={onToTap === undefined ? undefined : () => onToTap()}
          editable={activeSearchSlot === 'to'}
          query={searchQuery}
          onQueryChange={onSearchQueryChange}
        />
      </div>
      {onMyLocation !== undefined && <MyLocationButton variant="tablet" onClick={onMyLocation} />}
      <SwapButton variant="tablet" onClick={onSwap} />
    </div>
  )
}
